import { Column, Editing, Item, Lookup, RequiredRule, Toolbar } from 'devextreme-react/data-grid';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, Button as RxButton } from 'reactstrap';
import Breadcrumb from '../../../components/common/breadcrumb';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import api from '../../../services/api-configuration';
import { ButtonOnExporting } from '../../../utils/DataGridExporter';
import useFormatMessage from '../../../utils/useFormatMessage';

export const IlTanimlari = () => {
    const { t } = useFormatMessage();
    const [dataSources, setDataSources] = useState([]);
    const [countries, setCountries] = useState([]);
    const [subdivisions, setSubdivisions] = useState([]); // ISO 3166-2, the code of an il
    const ref = useRef();
    const mountedRef = useRef(true);

    useEffect(() => {
        fetchData();
        api.post("/city/getCountries")
            .then((r) => mountedRef.current && setCountries((r.data.data || []).map((c) => ({ ...c, label: `${c.code} – ${c.name_tr}` }))))
            .catch(() => toast.error("Ülke listesi alınamadı"));
        api.post("/city/getSubdivisions", {})
            .then((r) => mountedRef.current && setSubdivisions((r.data.data || []).map((s) => ({ ...s, label: `${s.code} – ${s.name} (${s.type})` }))))
            .catch(() => toast.error("ISO bölge listesi alınamadı"));
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const fetchData = async () => {
        try {
            const response = await api.post("/city/getAll");
            if (mountedRef.current) {
                let all_data = response.data.data || []
                setDataSources(
                    all_data.filter((item) => (item.id !== 7))
                );
            }
        } catch (error) {
            toast.error("Hata oluştu");
            console.error("Error fetching cities:", error);
        }
    };

    const addModel = async (data, e) => {
        try {
            data.create_user_id = parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'))
            data.city_code = "";

            const response = await api.post("/city/create", data);
            if (response.status === 200) {
                toast.success(t("Ekleme başarılı"));
                fetchData();
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error adding city:", error);
            let errorMessage = error.response?.data?.error || "";
            toast.error(errorMessage || "Hata oluştu");
            return false;
        }
    };

    const updateModel = async (e, data) => {
        try {
            const employeeId = parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'));
            const body = {
                ...data.oldData,
                ...data.newData,
                update_user_id: employeeId,
                update_date: new Date().toISOString()
            };

            const response = await api.post("/city/update", body);
            if (response.status === 200) {
                toast.success(t("Güncelleme başarılı"));
                fetchData();
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error updating city:", error);
            let errorMessage = error.response?.data?.error || "";
            toast.error(errorMessage || "Hata oluştu");
            return false;
        }
    };

    const onRowValidating = async (e) => {
        if (e.brokenRules.length === 0) {
            e.isValid = false;
            e.cancel = true;

            let success;
            if (!e.oldData) {
                success = await addModel(e.newData, e);
            } else {
                success = await updateModel(e, e);
            }

            if (success) {
                e.component.cancelEditData();
            }
        }
    };

    return (
        <>
            <Breadcrumb current={t("İl Tanımları")} />
            <Card className="p-4">
                <DatagridCustom
                    dataSource={dataSources}
                    reff={ref}
                    onRowValidating={onRowValidating}
                    onInitNewRow={(e) => {
                        e.data.is_active = true; // no default country: it must be chosen
                        e.component.__addingStart = true;
                    }}
                >
                    <Editing
                        mode="popup"
                        popup={{
                            title: t("Yeni İl Tanımı"),
                            showTitle: true,
                            width: 700,
                            height: "auto",
                        }}
                        allowUpdating
                        allowAdding
                        useIcons
                    />
                    <Column dataField="city_code" caption={t("Şehir Kodu")} selectedFilterOperation='contains' alignment='center'
                        allowEditing={false}
                        cellRender={(data) => {
                            if (data.text === "") {
                                return <i>OTOMATİK</i>
                            } else return <>{data.data.city_code}</>
                        }}
                    />
                    <Column dataField="city_name" caption={t("Şehir Adı")} selectedFilterOperation='contains' alignment='center'>
                        <RequiredRule />
                    </Column>
                    <Column dataField="country_code" caption={t("Ülke")} alignment='center'
                        setCellValue={(newData, value) => { newData.country_code = value; newData.iso_code = null; }}>
                        <Lookup dataSource={countries} valueExpr="code" displayExpr="label" />
                        <RequiredRule />
                    </Column>
                    {/* the il's ISO 3166-2 region becomes its code (TR-16, IT-GE); the list follows the chosen country */}
                    <Column dataField="iso_code" caption={t("ISO 3166-2 bölgesi")} alignment='center'>
                        <Lookup dataSource={(options) => ({ store: subdivisions, filter: options.data ? ['country_code', '=', options.data.country_code] : null })}
                            valueExpr="code" displayExpr="label" allowClearing />
                    </Column>
                    <Column dataField="kind" caption={t("Tür")} alignment='center' allowEditing={false}
                        cellRender={(d) => ({ IL: 'İl', GRUPAJ_BOLGESI: 'Grupaj bölgesi', SISTEM: 'Sistem kaydı' }[d.value] || '')} />
                    <Column dataField="grupaj_time" caption={t("Grupaj Süresi")} dataType="number" alignment='center' visible={false}>
                        <RequiredRule />
                    </Column>
                    <Column dataField="shipping_time" caption={t("Sevkiyat Süresi")} dataType="number" alignment='center' visible={false}>
                        <RequiredRule />
                    </Column>
                    <Column dataField="is_active" caption={t("Aktif")} dataType="boolean" selectedFilterOperation='contains' alignment='center'>
                        <RequiredRule />
                    </Column>

                    <Toolbar>
                        <Item name="addRowButton" />
                        <Item location="after">
                            <RxButton
                                color="light"
                                outline
                                onClick={fetchData}
                                style={{
                                    paddingInline: "10px",
                                    border: "1px solid #ddd"
                                }}
                            >
                                <i className="fa fa-refresh"></i>
                            </RxButton>
                            &nbsp;
                            <RxButton
                                className='btn btn-success'
                                style={{
                                    color: "white",
                                    paddingInline: "10px",
                                }}
                                onClick={(e) => ButtonOnExporting(e, ref, "İl Tanımları")}>
                                <i className="fa fa-file-excel-o"></i>
                            </RxButton>
                        </Item>
                    </Toolbar>
                </DatagridCustom>
            </Card>
        </>
    );
};


